import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

// Components
import {
  ReportsHeader,
  MetricCard,
  ImpressionsByState,
  OverviewSection,
  ChannelBreakdownCard,
  SessionsByCountry,
  AcquireUsersChart,
  TrafficSourcesChart,
  DataTable,
  ProfilePerformanceChart,
} from "./components";

import {
  ACQUIRE_CATEGORY_TABS,
  ACQUIRE_TABS,
  CAMPAIGNS_COLUMNS,
  TOP_CAMPAIGNS,
  PROFILE_PERFORMANCE_DATA,
  SOCIAL_FB_TABS,
  SOCIAL_FB_COLUMNS,
  SOCIAL_FB_DATA,
} from "./data/dummyData";

import LoadingSpinner from "../../../components/client/LoadingSpinner";
import {
  adaptReportsAnalyticsResponse,
  adaptSessionsByCountryResponse,
  adaptUserAcquisitionResponse,
  fetchReportsAnalytics,
  fetchSessionsByCountry,
  fetchUserAcquisitionChannels,
  getSafeEmptySessionsByCountry,
} from "../../../services/reportsAnalyticsService";

// ─── Campaign table custom cell renderer ─────────────────────────────────────

const renderCampaignCell = (key, value) => {
  if (key === "name")
    return <span style={{ color: "#101828", fontWeight: 500 }}>{value}</span>;
  if (key === "spend")
    return <span style={{ color: "#027A48", fontWeight: 600 }}>{value}</span>;
  return value;
};

// ─── Social Media table custom cell renderer ──────────────────────────────────

const renderSocialCell = (key, value) => {
  if (key === "impressions")
    return <span style={{ color: "#101828", fontWeight: 600 }}>{value}</span>;
  return value;
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const ReportsAnalytics = () => {
  const [socialTab, setSocialTab] = useState(SOCIAL_FB_TABS[0]);
  const user = useSelector((s) => s?.user);

  const [activeFilter, setActiveFilter] = useState({ type: "default" });

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [metricCards, setMetricCards] = useState([]);
  const [impressionsByState, setImpressionsByState] = useState({
    totalLabel: "",
    states: [],
    tooltip: null,
  });
  const [overview, setOverview] = useState({
    stats: [],
    lineData: { labels: [], datasets: [] },
  });
  const [channelBreakdowns, setChannelBreakdowns] = useState({
    leads: { labels: [], values: [], chartValues: [], pcts: [], colors: [] },
    impressions: {
      labels: [],
      values: [],
      chartValues: [],
      pcts: [],
      colors: [],
    },
    enrollments: {
      labels: [],
      values: [],
      chartValues: [],
      pcts: [],
      colors: [],
    },
    applications: {
      labels: [],
      values: [],
      chartValues: [],
      pcts: [],
      colors: [],
    },
  });
  const [trafficRadarData, setTrafficRadarData] = useState({
    labels: [],
    datasets: [],
  });
  const [sessionsByCountry, setSessionsByCountry] = useState(
    getSafeEmptySessionsByCountry(),
  );
  const [acquireUsers, setAcquireUsers] = useState({
    categoryTabs: ACQUIRE_CATEGORY_TABS,
    timeTabs: ACQUIRE_TABS,
    chartData: { labels: ["Total"], datasets: [] },
  });

  const requestPayload = useMemo(() => {
    const client_id = user?.client_id;
    const base = { client_id };
    switch (activeFilter.type) {
      case "1months":
        return { ...base, filter_type: "1months" };
      case "3months":
        return { ...base, filter_type: "3months" };
      case "6months":
        return { ...base, filter_type: "6months" };
      case "year":
        return { ...base, filter_type: "year" };
      case "custom":
        return {
          ...base,
          start_date: activeFilter.startDate,
          end_date: activeFilter.endDate,
        };
      default:
        return base;
    }
  }, [user?.client_id, activeFilter]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!requestPayload?.client_id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setPageError(null);

      try {
        const [analyticsResult, sessionsResult, acquisitionResult] =
          await Promise.allSettled([
            fetchReportsAnalytics(requestPayload),
            fetchSessionsByCountry(requestPayload),
            fetchUserAcquisitionChannels(requestPayload),
          ]);

        if (cancelled) return;

        const analyticsRes =
          analyticsResult.status === "fulfilled" ? analyticsResult.value : null;
        const sessionsRes =
          sessionsResult.status === "fulfilled" ? sessionsResult.value : null;
        const acquisitionRes =
          acquisitionResult.status === "fulfilled"
            ? acquisitionResult.value
            : null;

        // ── Primary data (analytics) ─────────────────────────────────────
        if (analyticsRes?.success) {
          const adapted = adaptReportsAnalyticsResponse(analyticsRes);
          setMetricCards(adapted.metricCards ?? []);
          const apiImpState = adapted.impressionsByState;
          if (apiImpState) {
            setImpressionsByState(apiImpState);
          }
          setOverview(
            adapted.overview ?? {
              stats: [],
              lineData: { labels: [], datasets: [] },
            },
          );
          setChannelBreakdowns(
            adapted.channelBreakdowns ?? {
              leads: {
                labels: [],
                values: [],
                chartValues: [],
                pcts: [],
                colors: [],
              },
              impressions: {
                labels: [],
                values: [],
                chartValues: [],
                pcts: [],
                colors: [],
              },
              enrollments: {
                labels: [],
                values: [],
                chartValues: [],
                pcts: [],
                colors: [],
              },
              applications: {
                labels: [],
                values: [],
                chartValues: [],
                pcts: [],
                colors: [],
              },
            },
          );
          setTrafficRadarData(
            adapted.trafficRadarData ?? { labels: [], datasets: [] },
          );
        } else {
          setPageError(analyticsRes?.error ?? "Failed to load analytics data");
        }

        // ── Secondary data (independent — won't block primary) ───────────
        if (sessionsRes?.success) {
          setSessionsByCountry(adaptSessionsByCountryResponse(sessionsRes));
        }
        if (acquisitionRes?.success) {
          setAcquireUsers(adaptUserAcquisitionResponse(acquisitionRes));
        }
      } catch (e) {
        if (cancelled) return;
        setPageError(e?.message ?? "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [requestPayload]);

  // Tab buttons rendered inside the Social Media card header
  const socialTabButtons = (
    <div className="d-flex gap-1">
      {SOCIAL_FB_TABS.map((t) => (
        <button
          key={t}
          onClick={() => setSocialTab(t)}
          style={{
            background: socialTab === t ? "#F9F5FF" : "transparent",
            color: socialTab === t ? "#6941C6" : "#667085",
            border: `1px solid ${socialTab === t ? "#D6BBFB" : "transparent"}`,
            borderRadius: 20,
            padding: "2px 10px",
            fontSize: "0.7rem",
            fontWeight: socialTab === t ? 600 : 400,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {t}
        </button>
      ))}
    </div>
  );

  return (
    <div style={{ minHeight: "100%" }}>
      {/* ── Dark header banner — paddingBottom = half card height (65px) ── */}
      <div
        style={{
          padding: "var(--spacing-xl) var(--spacing-lg)",
          paddingBottom: "110px",
          backgroundColor: "#302843",
        }}
      >
        <ReportsHeader
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          clientId={user?.client_id}
        />
      </div>

      {/* ── Metric Cards — pulled up by half card height so top half sits on dark bg ── */}
      <div
        className="row g-3"
        style={{
          marginTop: "-110px",
          position: "relative",
          zIndex: 1,
          padding: "0 var(--spacing-lg)",
          marginLeft: 0,
          marginRight: 0,
        }}
      >
        {metricCards.map((card) => (
          <div className="col-12 col-sm-6 col-xl-3" key={card.label}>
            <MetricCard {...card} />
          </div>
        ))}
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div
        style={{
          padding: "var(--spacing-lg)",
          paddingTop: "var(--spacing-xl)",
        }}
      >
        {loading && (
          <div className="d-flex justify-content-center py-5">
            <LoadingSpinner />
          </div>
        )}

        {!loading && pageError && (
          <div
            className="alert alert-warning"
            role="alert"
            style={{
              background: "#FFFAEB",
              borderColor: "#FEDF89",
              color: "#7A2E0E",
            }}
          >
            {pageError}
          </div>
        )}

        {/* Row 2: Impressions by State */}
        <div className="row g-3 mb-3">
          <div className="col-12">
            <ImpressionsByState
              states={impressionsByState.states}
              totalLabel={impressionsByState.totalLabel}
              tooltip={impressionsByState.tooltip}
            />
          </div>
        </div>

        {/* Row 3: Overview + Impressions by Device */}
        <div className="row g-3 mb-3">
          <div className="col-12">
            <OverviewSection
              title="Overview"
              stats={overview.stats}
              chartData={overview.lineData}
            />
          </div>
          <div className="col-12 col-lg-4">
            {/*
              Impressions by Device section hidden per request.
              Keeping the rest of the Reports & Analytics page intact.
            */}
            {/*
            <ImpressionsByDevice
              total={IMPRESSIONS_DEVICE_DATA.total}
              devices={IMPRESSIONS_DEVICE_DATA.devices}
            />
            */}
          </div>
        </div>

        {/* Row 4: Top Channels – Leads + Impressions */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <ChannelBreakdownCard
              title="Top Channels: Leads"
              data={channelBreakdowns.leads}
            />
          </div>
          <div className="col-12 col-md-6">
            <ChannelBreakdownCard
              title="Top Channels: Impressions"
              data={channelBreakdowns.impressions}
            />
          </div>
        </div>

        {/* Row 5: Top Channels – Enrollments + Applications */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <div
              style={{
                background: "#fff",
                border: "1px solid #F2F4F7",
                borderRadius: 12,
                padding: "24px",
                minHeight: 180,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#101828",
                  marginBottom: 16,
                }}
              >
                Top Channels: Enrollments
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#F4EBFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 2L12.09 7.26L18 8.27L14 12.14L14.91 18L10 15.27L5.09 18L6 12.14L2 8.27L7.91 7.26L10 2Z"
                      fill="#9E77ED"
                    />
                  </svg>
                </div>
                <span
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "#7F56D9",
                  }}
                >
                  Coming Soon
                </span>
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "#98A2B3",
                    textAlign: "center",
                  }}
                >
                  Enrollment channel data will be available here
                </span>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div
              style={{
                background: "#fff",
                border: "1px solid #F2F4F7",
                borderRadius: 12,
                padding: "24px",
                minHeight: 180,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#101828",
                  marginBottom: 16,
                }}
              >
                Top Channels: Applications
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#F4EBFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 2L12.09 7.26L18 8.27L14 12.14L14.91 18L10 15.27L5.09 18L6 12.14L2 8.27L7.91 7.26L10 2Z"
                      fill="#9E77ED"
                    />
                  </svg>
                </div>
                <span
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "#7F56D9",
                  }}
                >
                  Coming Soon
                </span>
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "#98A2B3",
                    textAlign: "center",
                  }}
                >
                  Application channel data will be available here
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Row 6: Sessions by Country + Acquire Users + Traffic Sources */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-4">
            <SessionsByCountry
              chartData={sessionsByCountry.chartData}
              total={sessionsByCountry.total}
            />
          </div>

          <div className="col-12 col-md-4">
            <AcquireUsersChart
              categoryTabs={acquireUsers.categoryTabs}
              timeTabs={acquireUsers.timeTabs}
              chartData={acquireUsers.chartData}
            />
          </div>

          <div className="col-12 col-md-4">
            <TrafficSourcesChart chartData={trafficRadarData} />
          </div>
        </div>

        {/* Row 7: Top Performing Campaigns */}
        {/* <div className="row g-3 mb-3">
          <div className="col-12">
            <DataTable
              title="Top Performing Campaigns"
              columns={CAMPAIGNS_COLUMNS}
              rows={TOP_CAMPAIGNS}
              renderCell={renderCampaignCell}
            />
          </div>
        </div> */}

        {/* Row 8: Profile Performance */}
        {/* <div className="row g-3 mb-3">
          <div className="col-12">
            <ProfilePerformanceChart chartData={PROFILE_PERFORMANCE_DATA} />
          </div>
        </div> */}

        {/* Row 9: Social Media – Facebook */}
        {/* <div className="row g-3">
          <div className="col-12">
            <DataTable
              title="Social Media - Facebook"
              columns={SOCIAL_FB_COLUMNS}
              rows={SOCIAL_FB_DATA}
              renderCell={renderSocialCell}
              extra={socialTabButtons}
            />
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default ReportsAnalytics;
